import { MobilePreownedFilters } from '../components/MobilePreownedFilters';
import { GenderFilter } from '../components/GenderFilter';
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import CatalogSidebar from '../components/CatalogSidebar';
import MobileCategoryGrid from '../components/MobileCategoryGrid';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { productsAPI, categoriesAPI } from '../services/api';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { trackAddToCart, trackViewItemList, trackSearch } from '../utils/analytics';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { addToGuestCart, isAuthenticated } = useCart();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [showSortDrawer, setShowSortDrawer] = useState(false);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [categoryNames, setCategoryNames] = useState({});
  const [totalProducts, setTotalProducts] = useState(0);

  const PER_PAGE_OPTIONS = [24, 48, 96];
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const parsedPer = parseInt(searchParams.get('per_page'), 10);
  const PAGE_SIZE = PER_PAGE_OPTIONS.includes(parsedPer) ? parsedPer : 24;

  const updateParams = (updates, replace = false) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace });
  };

  const goToPage = (p) => updateParams({ page: p });
  const changePageSize = (size) => updateParams({ per_page: size, page: 1 });

  const priceRanges = [
    { id: 1, label: 'Sub 100 Lei', min: 0, max: 100 },
    { id: 2, label: '100 - 500 Lei', min: 100, max: 500 },
    { id: 3, label: '500 - 1000 Lei', min: 500, max: 1000 },
    { id: 4, label: '1000 - 2500 Lei', min: 1000, max: 2500 },
    { id: 5, label: 'Peste 2500 Lei', min: 2500, max: null },
  ];

  const category = searchParams.get('category');
  const filter = searchParams.get('filter');
  const search = searchParams.get('search');

  // Filters derived from URL (shareable / bookmarkable / browser back support)
  const selectedBrands = (searchParams.get('brands') || '').split(',').filter(Boolean);
  const selectedGrades = (searchParams.get('grades') || '').split(',').filter(Boolean);
  const selectedStorages = (searchParams.get('storage') || '').split(',').filter(Boolean);
  const genderParam = searchParams.get('gen') || null;
  const compatBrand = searchParams.get('cbrand') || '';
  const selectedCompatModels = (searchParams.get('cmodel') || '').split(',').filter(Boolean);
  const selectedAttrs = (searchParams.get('attrs') || '').split('|').filter(Boolean)
    .reduce((acc, pair) => { const i = pair.indexOf(':'); if (i > 0) acc[pair.slice(0, i)] = pair.slice(i + 1); return acc; }, {});
  const priceId = parseInt(searchParams.get('price'), 10);
  const selectedPriceRange = priceRanges.find(r => r.id === priceId) || null;
  const sortBy = searchParams.get('sort') || 'default';
  const availParam = searchParams.get('avail');
  const selectedAvailability = availParam !== null
    ? availParam.split(',').filter(Boolean).reduce((acc, k) => ({ ...acc, [k]: true }), {})
    : { inStock: true };

  const setSelectedBrands = (arr) => updateParams({ brands: arr.length ? arr.join(',') : null, page: 1 });
  const setSelectedGrades = (arr) => updateParams({ grades: arr.length ? arr.join(',') : null, page: 1 });
  const setCompatBrand = (val) => updateParams({ cbrand: val || null, cmodel: null, page: 1 });
  const setSelectedCompatModels = (arr) => updateParams({ cmodel: arr.length ? arr.join(',') : null, page: 1 });
  const setAttrFilter = (key, value) => {
    const next = { ...selectedAttrs };
    if (value === null || selectedAttrs[key] === value) delete next[key]; else next[key] = value;
    const str = Object.entries(next).map(([k, v]) => `${k}:${v}`).join('|');
    updateParams({ attrs: str || null, page: 1 });
  };
  const setSelectedPriceRange = (range) => updateParams({ price: range ? range.id : null, page: 1 });
  const setSortBy = (val) => updateParams({ sort: val && val !== 'default' ? val : null, page: 1 });
  const setSelectedAvailability = (obj) => {
    const keys = Object.keys(obj).filter(k => obj[k]);
    updateParams({ avail: keys.join(','), page: 1 });
  };

  const [compatData, setCompatData] = useState({ brands: [], models: [] });
  const [attrFilters, setAttrFilters] = useState([]);

  useEffect(() => {
    if (!category) { setAttrFilters([]); return; }
    api.get(`/products/filters/attributes`, { params: { category } })
      .then(r => setAttrFilters(r.data.filters || []))
      .catch(() => setAttrFilters([]));
  }, [category]);

  useEffect(() => {
    productsAPI.getBrands(category).then(r => setAvailableBrands(r.data || [])).catch(() => setAvailableBrands([]));
  }, [category]);

  useEffect(() => {
    productsAPI.getCompatibility(category, compatBrand)
      .then(r => setCompatData(r.data || { brands: [], models: [] }))
      .catch(() => setCompatData({ brands: [], models: [] }));
  }, [category, compatBrand]);

  useEffect(() => {
    loadProducts();
  }, [searchParams]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = { with_total: true, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      
      // Apply filters
      if (category) params.category = category;
      if (search) params.search = search;
      
      // Special filters
      if (filter === 'oferte') {
        params.discount = true;
      } else if (filter === 'noutati') {
        params.is_new = true;
      }
      
      // Brand filter - use comma-separated list for multiple brands
      if (selectedBrands.length > 0) {
        params.brands = selectedBrands.join(',');
      }
      
      // Aesthetic grade filter (Pre-Owned phones)
      if (selectedGrades.length > 0) {
        params.grade = selectedGrades.join(',');
      }
      if (selectedStorages.length > 0) {
        params.storage = selectedStorages.join(',');
      }
      if (genderParam) {
        params.gender = genderParam;
      }

      // Compatibility filter (huse, folii, accesorii)
      if (compatBrand) {
        params.compat_brand = compatBrand;
      }
      if (selectedCompatModels.length > 0) {
        params.compat_model = selectedCompatModels.join(',');
      }

      // Filtre dinamice pe atribute (scheme aprobate per categorie)
      const attrEntries = Object.entries(selectedAttrs);
      if (attrEntries.length > 0) {
        params.spec_filter = attrEntries.map(([k, v]) => `${k}:${v}`).join(',');
      }
      
      // Price range
      if (selectedPriceRange) {
        params.min_price = selectedPriceRange.min;
        if (selectedPriceRange.max) {
          params.max_price = selectedPriceRange.max;
        }
      }
      
      // Availability filters - simple and clear
      if (selectedAvailability.inStock) {
        params.in_stock = true;
      }
      if (selectedAvailability.supplierStock) {
        params.supplier_stock = true;
      }
      if (selectedAvailability.isNew) {
        params.is_new = true;
      }
      if (selectedAvailability.isRefurbished) {
        params.is_refurbished = true;
      }
      if (selectedAvailability.outOfStock) {
        params.out_of_stock = true;
      }
      
      // Sorting
      const sortMapping = {
        'price-asc': 'price_asc',
        'price-desc': 'price_desc',
        'rating': 'rating',
        'name': 'name'
      };
      if (sortBy && sortBy !== 'default') {
        params.sort_by = sortMapping[sortBy];
      }
      
      const response = await productsAPI.getAll(params);
      const data = response.data;
      if (data && Array.isArray(data.products)) {
        setFilteredProducts(data.products);
        setTotalProducts(data.total || 0);
      } else {
        setFilteredProducts(data || []);
        setTotalProducts((data || []).length);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca produsele.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      if (isAuthenticated()) {
        // Logged in user - use API
        const { cartAPI } = await import('../services/api');
        await cartAPI.addItem({ productId: product._id, quantity: 1 });
      } else {
        // Guest user - use local storage
        addToGuestCart(product, 1);
      }
      
      // Track GA4 event
      trackAddToCart(product, 1);
      
      // Refresh cart count
      window.dispatchEvent(new Event('cartUpdated'));
      
      toast({
        title: 'Produs adăugat în coș!',
        description: `${product.name} a fost adăugat în coșul tău.`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul în coș.',
        variant: 'destructive',
      });
    }
  };

  const handleAddToWishlist = async (product) => {
    try {
      const { wishlistAPI } = await import('../services/api');
      await wishlistAPI.add({ productId: product._id });
      
      // Refresh wishlist count
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      toast({
        title: 'Adăugat la favorite!',
        description: `${product.name} a fost adăugat la lista ta de dorințe.`,
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul la favorite.',
        variant: 'destructive',
      });
    }
  };

  const handleBrandToggle = (brand) => {
    const next = selectedBrands.includes(brand)
      ? selectedBrands.filter(b => b !== brand)
      : [...selectedBrands, brand];
    setSelectedBrands(next);
  };

  const handleGradeToggle = (grade) => {
    const next = selectedGrades.includes(grade)
      ? selectedGrades.filter(g => g !== grade)
      : [...selectedGrades, grade];
    setSelectedGrades(next);
  };

  const handleStorageToggle = (s) => {
    const next = selectedStorages.includes(s) ? selectedStorages.filter((x) => x !== s) : [...selectedStorages, s];
    updateParams({ storage: next.length ? next.join(',') : null, page: 1 });
  };

  const handleCompatModelToggle = (model) => {
    const next = selectedCompatModels.includes(model)
      ? selectedCompatModels.filter(m => m !== model)
      : [...selectedCompatModels, model];
    setSelectedCompatModels(next);
  };

  const handleResetFilters = () => {
    updateParams({ brands: null, grades: null, storage: null, gen: null, price: null, sort: null, avail: null, cbrand: null, cmodel: null, page: 1 });
  };

  const handleAvailabilityChange = (key, checked) => {
    setSelectedAvailability({ ...selectedAvailability, [key]: checked });
  };

  const getCategoryName = () => {
    if (category) {
      // Real category NAME from DB (correct for deep subcategories like 'Lanseta');
      // fallback: derive from slug while loading
      return categoryNames[category]
        || category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    return 'Toate Produsele';
  };

  useEffect(() => {
    categoriesAPI.getAll().then(r => {
      const map = {};
      (r.data || []).forEach(c => { map[c.slug] = c.name; });
      setCategoryNames(map);
    }).catch(() => {});
  }, []);

  // ---- Dynamic SEO (title / description / canonical / prev-next) ----
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://r32.ro';
  const categoryName = getCategoryName();
  const brandLabel = selectedBrands.length === 1 ? selectedBrands[0] : (selectedBrands.length > 1 ? `${selectedBrands.length} branduri` : '');
  const pageSuffix = page > 1 ? ` - Pagina ${page}` : '';

  let seoTitle;
  let seoDesc;
  if (search) {
    seoTitle = `Rezultate pentru „${search}"${pageSuffix} | R32.ro`;
    seoDesc = `Produse găsite pentru căutarea „${search}" pe marketplace-ul R32.ro (peste 250.000 de produse): fashion, haine de firmă, încălțăminte, ochelari de soare, bebeluși și copii, cosmetice, parfumuri, IT, telefoane, huse, birotică, electronice și multe altele.`;
  } else if (filter === 'oferte') {
    seoTitle = `Oferte și reduceri${pageSuffix} | R32.ro`;
    seoDesc = 'Cele mai bune oferte și reduceri pe marketplace-ul R32.ro: fashion, haine de firmă, încălțăminte, ochelari de soare, produse pentru bebeluși și copii, cosmetice, parfumuri, IT, telefoane, huse, birotică și electronice la prețuri reduse.';
  } else if (filter === 'noutati') {
    seoTitle = `Noutăți${pageSuffix} | R32.ro`;
    seoDesc = 'Cele mai noi produse adăugate pe marketplace-ul R32.ro (peste 250.000 de produse): fashion, haine de firmă, încălțăminte, ochelari de soare, bebeluși și copii, cosmetice, parfumuri, IT, telefoane, huse, accesorii, birotică și electronice.';
  } else {
    const parts = [categoryName];
    if (brandLabel) parts.push(brandLabel);
    seoTitle = `${parts.join(' ')}${pageSuffix} | R32.ro`;
    seoDesc = `Cumpără ${brandLabel ? brandLabel + ' ' : ''}${categoryName.toLowerCase()} pe R32.ro, marketplace online cu peste 250.000 de produse: fashion, haine de firmă, încălțăminte, ochelari de soare, produse pentru bebeluși și copii, cosmetice, parfumuri, IT, telefoane, huse, birotică și electronice${selectedPriceRange ? ` (${selectedPriceRange.label})` : ''}. Livrare rapidă, plată securizată, retur în 14 zile.`;
  }

  const buildCanonical = (pageNum) => {
    const sp = new URLSearchParams();
    if (category) sp.set('category', category);
    if (search) sp.set('search', search);
    if (filter) sp.set('filter', filter);
    if (selectedBrands.length) sp.set('brands', selectedBrands.join(','));
    if (selectedPriceRange) sp.set('price', String(selectedPriceRange.id));
    if (pageNum > 1) sp.set('page', String(pageNum));
    const qs = sp.toString();
    return `${origin}/catalog${qs ? `?${qs}` : ''}`;
  };

  const totalPages = Math.ceil((totalProducts || 0) / PAGE_SIZE);
  const canonicalUrl = buildCanonical(page);
  const prevUrl = page > 1 ? buildCanonical(page - 1) : null;
  const nextUrl = page < totalPages ? buildCanonical(page + 1) : null;

  useSEO({
    title: seoTitle,
    description: seoDesc,
    canonical: canonicalUrl,
    prev: prevUrl,
    next: nextUrl,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="flex gap-4 lg:gap-6">
          {/* Sidebar - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:block">
            <CatalogSidebar 
              selectedBrands={selectedBrands}
              onBrandToggle={handleBrandToggle}
              selectedPriceRange={selectedPriceRange}
              onPriceRangeChange={setSelectedPriceRange}
              onResetFilters={handleResetFilters}
              selectedAvailability={selectedAvailability}
              onAvailabilityChange={handleAvailabilityChange}
              selectedGrades={selectedGrades}
              onGradeToggle={handleGradeToggle}
              selectedStorages={selectedStorages}
              onStorageToggle={handleStorageToggle}
              compatData={compatData}
              compatBrand={compatBrand}
              onCompatBrandChange={setCompatBrand}
              selectedCompatModels={selectedCompatModels}
              onCompatModelToggle={handleCompatModelToggle}
              attrFilters={attrFilters}
              selectedAttrs={selectedAttrs}
              onAttrToggle={setAttrFilter}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile: Category Grid + Subcategories */}
            <div className="lg:hidden mb-4">
              <MobileCategoryGrid currentCategory={category} />
            </div>

            {/* Header */}
            <div className="mb-4 sm:mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">
                {filter === 'oferte' ? 'Oferte Speciale' : 
                 filter === 'noutati' ? 'Noutăți' :
                 search ? `Rezultate pentru: "${search}"` :
                 getCategoryName()}
              </h1>
              <p className="text-sm sm:text-base text-gray-600" data-testid="catalog-total-count">{totalProducts.toLocaleString('ro-RO')} produse găsite</p>
            </div>
            <GenderFilter category={category} value={genderParam} onChange={(g) => updateParams({ gen: g, page: 1 })} />

            {/* Mobile: Filter & Sort Buttons */}
            {(
              <div className="lg:hidden flex gap-2 mb-4 sticky top-[104px] z-20 bg-gray-50/95 py-1 backdrop-blur">
                <button 
                  onClick={() => setShowFiltersDrawer(true)}
                  className="flex-1 bg-white border-2 border-gray-300 rounded-lg py-3 px-4 flex items-center justify-center space-x-2 hover:border-green-500 active:bg-gray-50"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="text-sm font-semibold">Filtrează</span>
                </button>
                <button 
                  onClick={() => setShowSortDrawer(true)}
                  className="flex-1 bg-white border-2 border-gray-300 rounded-lg py-3 px-4 flex items-center justify-center space-x-2 hover:border-green-500 active:bg-gray-50"
                >
                  <ChevronDown className="h-4 w-4" />
                  <span className="text-sm font-semibold">Ordonează</span>
                </button>
              </div>
            )}

            {/* Desktop Sort */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Produse pe pagină:</span>
                <Select value={String(PAGE_SIZE)} onValueChange={(v) => changePageSize(parseInt(v, 10))}>
                  <SelectTrigger className="w-24 rounded-xl border-2" data-testid="per-page-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PER_PAGE_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={String(opt)} data-testid={`per-page-${opt}`}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 hidden sm:inline">Sortează după:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36 sm:w-48 rounded-xl border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Implicit</SelectItem>
                    <SelectItem value="price-asc">Preț crescator</SelectItem>
                    <SelectItem value="price-desc">Preț descrescator</SelectItem>
                    <SelectItem value="rating">Rating</SelectItem>
                    <SelectItem value="name">Nume A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Se încarcă produsele...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onAddToWishlist={handleAddToWishlist}
                  />
                ))}
              </div>
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={totalProducts}
                onChange={goToPage}
              />
              </>
            ) : (
              <div className="p-12 text-center rounded-2xl bg-white border-2 border-gray-100" data-testid="catalog-empty-state">
                {(() => {
                  const hasBrand = selectedBrands.length > 0;
                  const hasPrice = !!selectedPriceRange;
                  const brandTxt = hasBrand ? `brandul ${selectedBrands.join(', ')}` : '';
                  const priceTxt = hasPrice ? `intervalul de preț „${selectedPriceRange.label}"` : '';
                  let msg;
                  if (hasBrand && hasPrice) {
                    msg = `Nu există produse pentru ${brandTxt} în ${priceTxt}.`;
                  } else if (hasBrand) {
                    msg = `Nu există produse pentru ${brandTxt}${category ? ' în această categorie' : ''}.`;
                  } else if (hasPrice) {
                    msg = `Nu există produse în ${priceTxt}${category ? ' în această categorie' : ''}.`;
                  } else if (search) {
                    msg = `Nu am găsit produse pentru căutarea „${search}".`;
                  } else {
                    msg = 'Nu există produse care să corespundă filtrelor selectate.';
                  }
                  return (
                    <>
                      <div className="text-5xl mb-4">🔍</div>
                      <p className="text-gray-800 text-lg font-semibold mb-1" data-testid="catalog-empty-message">{msg}</p>
                      <p className="text-gray-500 text-sm mb-6">Încearcă să ajustezi sau să elimini filtrele pentru mai multe rezultate.</p>
                      {(hasBrand || hasPrice || search) && (
                        <button
                          onClick={handleResetFilters}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors"
                          data-testid="catalog-empty-reset-btn"
                        >
                          Resetează filtrele
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Mobile Sort Drawer */}
      {showSortDrawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSortDrawer(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Ordonează după</h3>
              <button onClick={() => setShowSortDrawer(false)} className="text-gray-500">✕</button>
            </div>
            <div className="space-y-2">
              {[
                { value: 'default', label: 'Implicit' },
                { value: 'price-asc', label: 'Preț crescător' },
                { value: 'price-desc', label: 'Preț descrescător' },
                { value: 'rating', label: 'Rating' },
                { value: 'name', label: 'Nume A-Z' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSortBy(option.value);
                    setShowSortDrawer(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg ${
                    sortBy === option.value ? 'bg-green-50 text-green-600 font-semibold' : 'hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                  {sortBy === option.value && <span className="float-right">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Drawer */}
      {showFiltersDrawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowFiltersDrawer(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Filtre</h3>
              <button onClick={() => setShowFiltersDrawer(false)} className="text-gray-500">✕</button>
            </div>

            {/* Price Filters */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Preț</h4>
              <div className="space-y-2">
                {priceRanges.map(range => (
                  <label key={range.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="price"
                      checked={selectedPriceRange?.id === range.id}
                      onChange={() => setSelectedPriceRange(range)}
                      className="w-4 h-4 text-green-600"
                      data-testid={`mobile-price-${range.id}`}
                    />
                    <span className="text-sm">{range.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Brand Filters */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Brand</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableBrands.length === 0 ? (
                  <p className="text-sm text-gray-400">Nu există branduri disponibile.</p>
                ) : availableBrands.map(b => (
                  <label key={b.name} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(b.name)}
                      onChange={() => handleBrandToggle(b.name)}
                      className="w-4 h-4 text-green-600 rounded"
                      data-testid={`mobile-brand-${b.name}`}
                    />
                    <span className="text-sm flex-1">{b.name}</span>
                    <span className="text-xs text-gray-400">({b.count})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Compatibility Filters */}
            <MobilePreownedFilters category={category} selectedGrades={selectedGrades} onGradeToggle={handleGradeToggle}
              selectedStorages={selectedStorages} onStorageToggle={handleStorageToggle} />
            {compatData.brands.length > 0 && (
              <div className="mb-6" data-testid="mobile-compat-filter-group">
                <h4 className="font-semibold mb-3">Compatibilitate</h4>
                <div className="space-y-2 mb-3">
                  {compatData.brands.map(b => (
                    <label key={b.name} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="compat-brand"
                        checked={compatBrand === b.name}
                        onChange={() => setCompatBrand(compatBrand === b.name ? '' : b.name)}
                        onClick={() => { if (compatBrand === b.name) setCompatBrand(''); }}
                        className="w-4 h-4 text-green-600"
                        data-testid={`mobile-compat-brand-${b.name}`}
                      />
                      <span className="text-sm flex-1">{b.name}</span>
                      <span className="text-xs text-gray-400">({b.count})</span>
                    </label>
                  ))}
                </div>
                {compatBrand && compatData.models.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto border-t pt-2">
                    {compatData.models.map(m => (
                      <label key={m.name} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCompatModels.includes(m.name)}
                          onChange={() => handleCompatModelToggle(m.name)}
                          className="w-4 h-4 text-green-600 rounded"
                          data-testid={`mobile-compat-model-${m.name}`}
                        />
                        <span className="text-sm flex-1">{m.name}</span>
                        <span className="text-xs text-gray-400">({m.count})</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <button
                onClick={handleResetFilters}
                className="flex-1 border-2 border-gray-300 text-gray-700 rounded-lg py-3 font-semibold"
              >
                Resetează
              </button>
              <button
                onClick={() => setShowFiltersDrawer(false)}
                className="flex-1 bg-green-600 text-white rounded-lg py-3 font-semibold"
              >
                Aplică Filtre
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Catalog;
